import React, {
  useMemo,
  useState,
  useContext,
  useEffect,
  useCallback,
  useRef,
} from "react";
import toast, { Toaster } from "react-hot-toast";
import { useNavigate, useLocation } from "react-router-dom";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
} from "@tanstack/react-table";
import {
  Settings2,
  MapPin,
  RefreshCw,
  Building2,
  Phone,
  Hash,
  BadgeCheck,
  BadgeX,
  CalendarClock,
  CreditCard,
  Store,
  UserRound,
} from "lucide-react";
import RestaurantLatLongModal from "./RestaurantLatLongModal";
import useClients from "../Services/ClientServices/useClient";
import ClientOrgTypeTable from "./ClientOrgTypeTable";
import OnboardingRestaurantPanel from "./OnboardingRestaurantPanel";
import { GlobalContext } from "../../Context/GlobalContext";

export default function AdminClients({ mode }) {
  const {
    clients,
    loading,
    fetchAll,
    updatePaymentGateway,
    getRestaurantPreciseLocation,
    updateRestaurantLatLong,
    getOrganizationTypeAdmin,
    getAllOrgByTypeAdmin,
    globalSearchAllClientList,
    onboardRestaurantStep,
    getOnboardedRestaurantDetails,
    getRestaurantLocation,
    updateOrganizationLocation,
  } = useClients();

  const ONBOARDING_RESTAURANT_TAB = "onboarding_restaurant";
  const { GateWay } = useContext(GlobalContext);
  const navigate = useNavigate();
  const location = useLocation();

  // Filters
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [date, setDate] = useState("");
  const [activeTab, setActiveTab] = useState("");
  const [locationOptions, setLocationOptions] = useState([]); // [{ id, name }]
  const [locationFilter, setLocationFilter] = useState("");
  const [assignOpenFor, setAssignOpenFor] = useState(null);
  const [assigning, setAssigning] = useState(false);
  const [assignClient, setAssignClient] = useState(null);
  const [assignMenuPos, setAssignMenuPos] = useState(null); // { top, left }

  const PAGE_SIZE = 100;

  const [allClientsPage, setAllClientsPage] = useState(1);
  const [orgPage, setOrgPage] = useState(1);

  const [orgTotalRecords, setOrgTotalRecords] = useState(0);
  const [orgTabs, setOrgTabs] = useState([]);
  const [orgRows, setOrgRows] = useState([]);
  const [orgLoading, setOrgLoading] = useState(false);
  const [activeFilter, setActiveFilter] = useState("y");

  const [globalSearchRows, setGlobalSearchRows] = useState([]);
  const [globalSearchLoading, setGlobalSearchLoading] = useState(false);
  const globalSearchRequestRef = useRef(0);

  const normalizedSearch = String(q || "")
    .trim()
    .toLowerCase();

  const isGlobalSearchActive = normalizedSearch.length > 0;

  const filteredOrgRows = useMemo(() => {
    if (!locationFilter) return orgRows || [];
    if (locationFilter === "__none__") {
      return (orgRows || []).filter(
        (row) => !String(row?.location ?? "").trim(),
      );
    }
    return (orgRows || []).filter(
      (row) =>
        String(row?.location ?? "")
          .trim()
          .toLowerCase() === locationFilter.toLowerCase(),
    );
  }, [orgRows, locationFilter]);

  const clientRows = useMemo(() => {
    const localRows = Array.isArray(clients) ? clients : [];
    const serverRows = Array.isArray(globalSearchRows) ? globalSearchRows : [];

    if (!isGlobalSearchActive) {
      return localRows;
    }

    const merged = [...serverRows, ...localRows];
    const seen = new Set();

    return merged.filter((client, index) => {
      const key = String(client?.us_code || client?.id || `client-${index}`);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [clients, globalSearchRows, isGlobalSearchActive]);

const filtered = useMemo(() => {
  const words = normalizedSearch.split(/\s+/).filter(Boolean);

  // id → name map from master locations
  const locationIdToName = new Map(
    (locationOptions || []).map((o) => [String(o.id), String(o.name).trim()]),
  );
  const locationNameSet = new Set(
    (locationOptions || []).map((o) => String(o.name).trim().toLowerCase()),
  );

  return (clientRows || []).filter((client) => {
    const statusText =
      String(client?.us_status || "") === "1" ? "active" : "inactive";

    const searchableText = [
      client?.id,
      client?.us_code,
      client?.us_loginid,
      client?.us_email,
      client?.us_name,
      client?.us_status,
      statusText,
      client?.us_last_login,
      client?.payment_gateway,
      client?.organization_name,
      client?.qr_code,
      client?.deep_link,
      client?.balance,
      client?.totalCampaigns,
      client?.totalTemplates,
      client?.location,
      client?.location_name,
    ]
      .map((value) =>
        String(value ?? "")
          .trim()
          .toLowerCase(),
      )
      .filter(Boolean)
      .join(" ");

    const bySearch =
      words.length === 0 ||
      words.every((word) => searchableText.includes(word));
    const byStatus =
      !status || String(client?.us_status || "") === String(status);
    const byDate = !date || String(client?.us_last_login || "").includes(date);

    // ---- Location filter (works with name OR id) ----
    const rawLocation = String(
      client?.location ?? client?.location_name ?? client?.fk_location_id ?? "",
    ).trim();

    let byLocation = true;
    if (locationFilter) {
      if (locationFilter === "__none__") {
        byLocation = !rawLocation;
      } else {
        const filterName = locationFilter.trim().toLowerCase();

        // Resolve client value: if it's an id, map to name; else use as-is
        const resolvedName = (
          locationIdToName.get(rawLocation) || rawLocation
        )
          .trim()
          .toLowerCase();

        byLocation =
          resolvedName === filterName ||
          rawLocation.toLowerCase() === filterName;
      }
    }

    return bySearch && byStatus && byDate && byLocation;
  });
}, [
  clientRows,
  normalizedSearch,
  status,
  date,
  locationFilter,
  locationOptions,
]);


  const allClientsTotalPages = Math.max(
    1,
    Math.ceil(filtered.length / PAGE_SIZE),
  );

  const paginatedClients = useMemo(() => {
    const startIndex = (allClientsPage - 1) * PAGE_SIZE;
    const endIndex = startIndex + PAGE_SIZE;
    return filtered.slice(startIndex, endIndex);
  }, [filtered, allClientsPage]);

  const [gatewayAllOpen, setGatewayAllOpen] = useState(false);
  const [gatewayAll, setGatewayAll] = useState("");
  const [latLongOpen, setLatLongOpen] = useState(false);
  const [savingAllGateway, setSavingAllGateway] = useState(false);

  const adminId = useMemo(() => {
    try {
      const raw = localStorage.getItem("Admin");
      if (!raw) return undefined;
      return JSON.parse(raw)?.pkid;
    } catch {
      return undefined;
    }
  }, []);

  const closeAssignMenu = useCallback(() => {
    setAssignOpenFor(null);
    setAssignClient(null);
    setAssignMenuPos(null);
  }, []);

  const handleUpdateAllGateway = async () => {
    if (!gatewayAll) return;
    setSavingAllGateway(true);
    try {
      await updatePaymentGateway({
        adminId,
        userid: "ALL",
        paymentGateway: gatewayAll,
      });
      setGatewayAllOpen(false);
      setGatewayAll("");
    } finally {
      setSavingAllGateway(false);
    }
  };

  const handleConfigureClient = useCallback(
    (client) => {
      if (!client) return;

      const clientId = String(
        client.us_code ||
          client.user_code ||
          client.userid ||
          client.user_id ||
          client.userId ||
          client.code ||
          client.client_code ||
          client.fk_user_id ||
          "",
      ).trim();

      if (!clientId) {
        console.log("Configure client row:", client);
        toast.error("Client code is not available");
        return;
      }

      const organizationName =
        client.organization_name ||
        client.restaurant_name ||
        client.org_name ||
        client.us_name ||
        client.name ||
        "";

      const paymentGateway =
        client.payment_gateway || client.paymentGateway || client.gateway || "";

      const normalizedClient = {
        ...client,
        us_code: clientId,
        user_code: clientId,
        organization_name: organizationName,
        payment_gateway: paymentGateway,
        us_name:
          client.us_name ||
          client.owner_name ||
          client.user_name ||
          organizationName,
        us_loginid:
          client.us_loginid ||
          client.whatsapp_number ||
          client.mobile ||
          client.phone_no ||
          client.phone ||
          "",
      };

      const token =
        window.crypto?.randomUUID?.() ||
        `${Date.now()}-${Math.random().toString(36).slice(2)}`;

      sessionStorage.setItem(
        `client_config_${token}`,
        JSON.stringify({
          client: normalizedClient,
          clientId,
          organizationName,
          paymentGateway,
          createdAt: Date.now(),
          openedFrom: mode === "developer" ? "developer" : "clients",
        }),
      );

      const configBasePath =
        mode === "developer"
          ? "/admin/developer/client-configuration"
          : "/admin/clients/client-configuration";

      navigate(`${configBasePath}/${token}`, {
        state: { token },
      });
    },
    [navigate, mode],
  );

  const loadOrgByType = async (
    tabId,
    activeValue = activeFilter,
    page = orgPage,
  ) => {
    if (!tabId || tabId === "all" || tabId === ONBOARDING_RESTAURANT_TAB) {
      return;
    }

    setOrgLoading(true);

    try {
      const result = await getAllOrgByTypeAdmin({
        orgType: tabId,
        active: activeValue,
        pageIndex: page,
        recordPerPage: PAGE_SIZE,
        limit: PAGE_SIZE,
        offset: (page - 1) * PAGE_SIZE,
      });

      const responseCode = result?.responseCode ?? result?.data?.responseCode;
      const responseData = result?.response ?? result?.data?.response;

      const rows = Array.isArray(responseData)
        ? responseData
        : Array.isArray(responseData?.data)
          ? responseData.data
          : Array.isArray(responseData?.rows)
            ? responseData.rows
            : Array.isArray(responseData?.list)
              ? responseData.list
              : [];

      const totalRecords = Number(
        result?.totalRecords ??
          result?.totalRecord ??
          result?.total_count ??
          result?.total ??
          responseData?.totalRecords ??
          responseData?.totalRecord ??
          responseData?.total_count ??
          responseData?.total ??
          rows.length,
      );

      if (responseCode === "00") {
        setOrgRows(rows);
        setOrgTotalRecords(totalRecords);
        setOrgPage(page);
      } else {
        setOrgRows([]);
        setOrgTotalRecords(0);
      }
    } catch (error) {
      console.error("getAllOrgByTypeAdmin error:", error);
      setOrgRows([]);
      setOrgTotalRecords(0);
      toast.error("Could not load organizations");
    } finally {
      setOrgLoading(false);
    }
  };

  const handleAssignLocation = useCallback(
    async (client, locationId) => {
      const pkInfoId =
        client?.pk_info_id ??
        client?.pkInfoId ??
        client?.info_id ??
        client?.id;

      if (!pkInfoId) {
        toast.error("Organization info id not found");
        return;
      }
      if (!locationId) return;

      setAssigning(true);
      try {
        const res = await updateOrganizationLocation({
          pk_info_id: String(pkInfoId),
          location: String(locationId),
        });

        if (res?.responseCode === "00") {
          toast.success(res?.responseMessage || "Location assigned");
          setAssignOpenFor(null);
          setAssignClient(null);
          setAssignMenuPos(null);
          if (activeTab === "all" || isGlobalSearchActive) {
            fetchAll();
          } else if (activeTab && activeTab !== ONBOARDING_RESTAURANT_TAB) {
            loadOrgByType(activeTab, activeFilter, orgPage);
          }
        } else {
          toast.error(res?.responseMessage || "Failed to assign location");
        }
      } catch (e) {
        console.error(e);
        toast.error("Error assigning location");
      } finally {
        setAssigning(false);
      }
    },
    [
      updateOrganizationLocation,
      activeTab,
      isGlobalSearchActive,
      fetchAll,
      activeFilter,
      orgPage,
    ],
  );

  // Load master locations once on mount
  useEffect(() => {
    let cancelled = false;

    const loadLocations = async () => {
      if (typeof getRestaurantLocation !== "function") return;
      try {
        const res = await getRestaurantLocation({});
        if (cancelled) return;
        if (res?.responseCode === "00" && Array.isArray(res.response)) {
          const opts = res.response
            .filter((item) => String(item?.status || "").toUpperCase() === "Y")
            .map((item) => ({
              id: String(item?.pk_ml_id ?? ""),
              name: String(item?.location || "").trim(),
            }))
            .filter((o) => o.id && o.name);
          const map = new Map();
          opts.forEach((o) => map.set(o.id, o));
          setLocationOptions(
            Array.from(map.values()).sort((a, b) =>
              a.name.localeCompare(b.name),
            ),
          );
        } else {
          setLocationOptions([]);
        }
      } catch (e) {
        if (!cancelled) {
          console.error("load locations error:", e);
          setLocationOptions([]);
        }
      }
    };

    loadLocations();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Close assign menu on outside click / scroll
  useEffect(() => {
    if (!assignOpenFor) return;

    const onDown = (e) => {
      if (e.target.closest?.("[data-assign-menu]")) return;
      closeAssignMenu();
    };

    const onScroll = () => {
      closeAssignMenu();
    };

    document.addEventListener("mousedown", onDown);
    window.addEventListener("scroll", onScroll, true);
    return () => {
      document.removeEventListener("mousedown", onDown);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [assignOpenFor, closeAssignMenu]);

  useEffect(() => {
    fetchAll();
  }, [location.pathname]);

  useEffect(() => {
    const searchValue = String(q || "").trim();

    if (!searchValue) {
      globalSearchRequestRef.current += 1;
      setGlobalSearchRows([]);
      setGlobalSearchLoading(false);
      return undefined;
    }

    const requestId = globalSearchRequestRef.current + 1;
    globalSearchRequestRef.current = requestId;

    const timer = window.setTimeout(async () => {
      setGlobalSearchLoading(true);

      try {
        const result = await globalSearchAllClientList(searchValue);

        if (globalSearchRequestRef.current !== requestId) return;

        const responseRows = Array.isArray(result?.response)
          ? result.response
          : Array.isArray(result?.data?.response)
            ? result.data.response
            : [];

        if (
          result?.responseCode === "00" ||
          result?.data?.responseCode === "00"
        ) {
          setGlobalSearchRows(responseRows);
        } else {
          setGlobalSearchRows([]);
        }
      } catch (error) {
        if (globalSearchRequestRef.current !== requestId) return;
        console.error("Global client search error:", error);
        setGlobalSearchRows([]);
      } finally {
        if (globalSearchRequestRef.current === requestId) {
          setGlobalSearchLoading(false);
        }
      }
    }, 500);

    return () => window.clearTimeout(timer);
  }, [q, globalSearchAllClientList]);

  useEffect(() => {
    setAllClientsPage(1);
  }, [q, status, date]);

  const columns = useMemo(
    () => [
      {
        header: "Client",
        id: "client",
        cell: ({ row }) => {
          const r = row.original;
          return (
            <div className="flex items-center gap-3">
              <div className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl border bg-emerald-50 text-[#01C37D]">
                <Building2 size={19} />
              </div>
              <div>
                <div className="font-semibold text-gray-900">
                  {r.organization_name || "—"}
                </div>
                <div className="mt-1 flex items-center gap-1 text-xs text-gray-500">
                  <UserRound size={12} />
                  {r.us_name || "—"}
                </div>
                <div className="mt-1 flex items-center gap-1 text-xs text-gray-500">
                  <Phone size={12} />
                  {r.us_contact || r.us_loginid || "—"}
                </div>
              </div>
            </div>
          );
        },
      },
      {
        header: "Code / ID",
        id: "code",
        cell: ({ row }) => {
          const r = row.original;
          return (
            <div>
              <div className="inline-flex items-center gap-1 rounded-xl bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-700">
                <Hash size={13} />
                {r.us_code || "—"}
              </div>
              <div className="mt-1 text-xs text-gray-500">
                ID: {r.id || "—"}
              </div>
            </div>
          );
        },
      },
      {
        header: "Gateway",
        id: "gateway",
        cell: ({ row }) => {
          const r = row.original;
          return (
            <div>
              <div className="inline-flex items-center gap-1 rounded-xl border border-violet-100 bg-violet-50 px-2.5 py-1 text-xs font-semibold text-violet-700">
                <CreditCard size={13} />
                {r.payment_gateway || "—"}
              </div>
            </div>
          );
        },
      },
      {
        header: "Login / Status",
        id: "status",
        cell: ({ row }) => {
          const r = row.original;
          const isActive = r.us_status === "1";
          return (
            <div>
              <div
                className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-semibold ${
                  isActive
                    ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                    : "border-rose-200 bg-rose-50 text-rose-700"
                }`}
              >
                {isActive ? <BadgeCheck size={13} /> : <BadgeX size={13} />}
                {isActive ? "Active" : "Inactive"}
              </div>
              <div className="mt-1 flex items-center gap-1 text-xs text-gray-500">
                <CalendarClock size={12} />
                {r.us_last_login || "—"}
              </div>
            </div>
          );
        },
      },
      {
        header: "Action",
        id: "act",
        cell: ({ row }) => {
          const r = row.original;
          const rowKey = String(
            r?.pk_info_id ?? r?.id ?? r?.us_code ?? row.id,
          );

          return (
            <div className="flex justify-end gap-2">
              <button
                type="button"
                className="inline-flex h-9 items-center gap-2 rounded-xl border px-3 text-xs font-medium hover:bg-gray-50"
                onClick={() => handleConfigureClient(r)}
              >
                <Settings2 size={14} />
                Configure
              </button>

              <button
                type="button"
                className="inline-flex h-9 items-center gap-2 rounded-xl border px-3 text-xs font-medium hover:bg-gray-50"
                onClick={(e) => {
                  e.stopPropagation();
                  const rect = e.currentTarget.getBoundingClientRect();
                  if (assignOpenFor === rowKey) {
                    closeAssignMenu();
                  } else {
                    setAssignOpenFor(rowKey);
                    setAssignClient(r);
                    setAssignMenuPos({
                      top: rect.bottom + 4,
                      left: Math.max(8, rect.right - 192),
                    });
                  }
                }}
              >
                <MapPin size={14} />
                Assign
              </button>
            </div>
          );
        },
      },
    ],
    [handleConfigureClient, assignOpenFor, closeAssignMenu],
  );

  const table = useReactTable({
    data: paginatedClients,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const hasActiveFilters = q || status || date || locationFilter;

  useEffect(() => {
    const loadTabs = async () => {
      try {
        const res = await getOrganizationTypeAdmin();

        if (res?.responseCode !== "00") {
          setOrgTabs([]);
          return;
        }

        const tabs = Array.isArray(res.response) ? res.response : [];
        setOrgTabs(tabs);

        const restaurantTab = tabs.find((tab) =>
          String(tab?.restro_type || "")
            .trim()
            .toLowerCase()
            .includes("restaurant"),
        );

        if (restaurantTab?.id) {
          setActiveTab(restaurantTab.id);
          setOrgPage(1);
          await loadOrgByType(restaurantTab.id, activeFilter, 1);
        } else {
          setActiveTab("all");
        }
      } catch (error) {
        console.error("Organization tabs error:", error);
        setActiveTab("all");
      }
    };

    loadTabs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTabChange = async (tabId) => {
    setActiveTab(tabId);
    setQ("");
    setStatus("");
    setDate("");
    setLocationFilter("");
    closeAssignMenu();
    setAllClientsPage(1);
    setOrgPage(1);

    if (tabId === "all" || tabId === ONBOARDING_RESTAURANT_TAB) {
      setOrgRows([]);
      setOrgTotalRecords(0);
      return;
    }

    await loadOrgByType(tabId, activeFilter, 1);
  };

  const handleActiveChange = async (value) => {
    setActiveFilter(value);
    setOrgPage(1);
    setLocationFilter("");

    if ( activeTab !== ONBOARDING_RESTAURANT_TAB) {
      await loadOrgByType(activeTab, value, 1);
    }
  };

  return (
    <div className="p-3 lg:p-5">
      <Toaster position="top-right" />
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="grid h-9 w-9 place-items-center rounded-xl bg-[#01C37D]/10 text-[#01C37D]">
            AC
          </div>
          <div>
            <h1 className="text-xl font-semibold">All Clients</h1>
            <p className="text-sm text-gray-500">
              Search, filter, and manage client actions
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            className="inline-flex h-9 items-center gap-2 rounded-xl border px-3 text-sm hover:bg-gray-50"
            onClick={() => setGatewayAllOpen(true)}
          >
            <Settings2 size={16} />
            Update Gateway
          </button>
          <button
            className="inline-flex h-9 items-center gap-2 rounded-xl border px-3 text-sm hover:bg-gray-50"
            onClick={() => setLatLongOpen(true)}
          >
            <MapPin size={16} />
            Update Lat & Lon
          </button>
          <button
            className="h-9 rounded-xl border px-3 text-sm hover:bg-gray-50"
            onClick={fetchAll}
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-3 rounded-2xl border bg-white p-2">
        <div className="flex flex-wrap items-center gap-2">
          {orgTabs.map((tab) => (
            <button
              key={tab.id}
              className={`rounded-xl px-4 py-2 text-sm font-medium ${
                activeTab === tab.id
                  ? "bg-[#01C37D] text-white"
                  : "bg-gray-50 text-gray-700 hover:bg-gray-100"
              }`}
              onClick={() => handleTabChange(tab.id)}
            >
              {tab.restro_type}
            </button>
          ))}

          <button
            className={`rounded-xl px-4 py-2 text-sm font-medium ${
              activeTab === "all"
                ? "bg-[#01C37D] text-white"
                : "bg-gray-50 text-gray-700 hover:bg-gray-100"
            }`}
            onClick={() => handleTabChange("all")}
          >
            All Clients
          </button>

          <button
            className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-medium ${
              activeTab === ONBOARDING_RESTAURANT_TAB
                ? "bg-[#01C37D] text-white"
                : "bg-gray-50 text-gray-700 hover:bg-gray-100"
            }`}
            onClick={() => handleTabChange(ONBOARDING_RESTAURANT_TAB)}
          >
            <Store size={15} />
            Onboarding Restaurant
          </button>

          {activeTab !== ONBOARDING_RESTAURANT_TAB && (
            <div className="ml-auto flex items-center gap-2">
              {activeTab !== "all" && (
                <select
                  className="h-9 rounded-xl border bg-white px-3 text-sm outline-none"
                  value={activeFilter}
                  onChange={(e) => handleActiveChange(e.target.value)}
                >
                  <option value="y">Active</option>
                  <option value="N">Inactive</option>
                  <option value="0">All</option>
                </select>
              )}
              <button
                className="inline-flex h-9 items-center gap-2 rounded-xl border px-3 text-sm hover:bg-gray-50"
                onClick={() =>
                  activeTab === "all"
                    ? fetchAll()
                    : loadOrgByType(activeTab, activeFilter, orgPage)
                }
              >
                <RefreshCw size={15} />
                Refresh
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Filters */}
      {activeTab !== ONBOARDING_RESTAURANT_TAB && (
        <div className="mb-3 rounded-2xl border bg-white p-3">
          <div className="flex flex-wrap items-end gap-2">
            <label className="text-sm">
              <div className="mb-1 text-xs text-gray-500">Search</div>
              <input
                className="h-10 w-64 rounded-xl border px-3 text-sm outline-none"
                placeholder="Search globally by User ID / Code / Name"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
            </label>
            {globalSearchLoading ? (
              <div className="pb-2 text-xs font-medium text-[#01C37D]">
                Searching clients…
              </div>
            ) : null}
            <label className="text-sm">
              <div className="mb-1 text-xs text-gray-500">Status</div>
              <select
                className="h-10 w-40 rounded-xl border bg-white px-3 text-sm outline-none"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
              >
                <option value="">All</option>
                <option value="1">Active</option>
                <option value="0">Inactive</option>
              </select>
            </label>
            <label className="text-sm">
              <div className="mb-1 text-xs text-gray-500">Last Login</div>
              <input
                type="date"
                className="h-10 w-44 rounded-xl border px-3 text-sm outline-none"
                value={date}
                onChange={(e) => setDate(e.target.value)}
              />
            </label>

            
              <label className="text-sm">
                <div className="mb-1 text-xs text-gray-500">Location</div>
                <select
                  className="h-10 min-w-[160px] rounded-xl border bg-white px-3 text-sm outline-none"
                  value={locationFilter}
                  onChange={(e) => setLocationFilter(e.target.value)}
                >
                  <option value="">All Locations</option>
                 
                  {locationOptions.map((loc) => (
                    <option key={loc.id} value={loc.name}>
                      {loc.name}
                    </option>
                  ))}
                </select>
              </label>
            

            <div className="ml-auto flex gap-2">
              <button
                className="h-10 rounded-xl border px-3 text-sm hover:bg-gray-50"
                onClick={() => {
                  setQ("");
                  setStatus("");
                  setDate("");
                  setLocationFilter("");
                }}
              >
                Reset
              </button>
            </div>
          </div>
          {hasActiveFilters ? (
            <div className="mt-2 flex flex-wrap gap-2">
              {q && <Badge onClear={() => setQ("")}>Search: {q}</Badge>}
              {status && (
                <Badge onClear={() => setStatus("")}>
                  Status: {status === "1" ? "Active" : "Inactive"}
                </Badge>
              )}
              {date && <Badge onClear={() => setDate("")}>Date: {date}</Badge>}
              {locationFilter && (
                <Badge onClear={() => setLocationFilter("")}>
                  Location:{" "}
                  {locationFilter === "__none__"
                    ? "No Location"
                    : locationFilter}
                </Badge>
              )}
            </div>
          ) : null}
        </div>
      )}

      {(loading || globalSearchLoading) &&
      activeTab !== ONBOARDING_RESTAURANT_TAB ? (
        <div className="grid place-items-center rounded-2xl border bg-white py-10 text-sm text-gray-500">
          Loading…
        </div>
      ) : activeTab === ONBOARDING_RESTAURANT_TAB ? (
        <OnboardingRestaurantPanel
          clients={clients || []}
          gateways={GateWay || []}
          onboardRestaurantStep={onboardRestaurantStep}
          getOnboardedRestaurantDetails={getOnboardedRestaurantDetails}
          mode={mode}
        />
      ) : activeTab === "all" || isGlobalSearchActive ? (
        <div className="rounded-2xl border bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="bg-[#01C37D] text-white">
                {table.getHeaderGroups().map((hg) => (
                  <tr key={hg.id}>
                    {hg.headers.map((h) => (
                      <th
                        key={h.id}
                        className={`px-4 py-3 font-semibold ${
                          h.column.id === "act" ? "text-right" : ""
                        }`}
                      >
                        {flexRender(h.column.columnDef.header, h.getContext())}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map((r) => (
                  <tr
                    key={r.id}
                    className="border-t transition hover:bg-emerald-50/40"
                  >
                    {r.getVisibleCells().map((c) => (
                      <td key={c.id} className="px-4 py-3 align-middle">
                        {flexRender(c.column.columnDef.cell, c.getContext())}
                      </td>
                    ))}
                  </tr>
                ))}
                {!paginatedClients.length && (
                  <tr>
                    <td
                      className="px-4 py-8 text-center text-gray-500"
                      colSpan={columns.length}
                    >
                      No clients found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <Pagination
            currentPage={allClientsPage}
            totalPages={allClientsTotalPages}
            totalRecords={filtered.length}
            pageSize={PAGE_SIZE}
            onPageChange={setAllClientsPage}
            loading={loading || globalSearchLoading}
          />
        </div>
      ) : (
        <div className="overflow-hidden rounded-2xl border bg-white shadow-sm">
          <ClientOrgTypeTable
            rows={filteredOrgRows}
            loading={orgLoading}
            search={q}
            onConfigure={handleConfigureClient}
            currentPage={orgPage}
            pageSize={PAGE_SIZE}
          />

          <Pagination
            currentPage={orgPage}
            totalPages={Math.max(
              1,
              Math.ceil(
                (locationFilter ? filteredOrgRows.length : orgTotalRecords) /
                  PAGE_SIZE,
              ),
            )}
            totalRecords={
              locationFilter ? filteredOrgRows.length : orgTotalRecords
            }
            pageSize={PAGE_SIZE}
            loading={orgLoading}
            onPageChange={(nextPage) =>
              loadOrgByType(activeTab, activeFilter, nextPage)
            }
          />
        </div>
      )}

      {gatewayAllOpen && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40">
          <div className="w-full max-w-sm rounded-2xl bg-white p-4 shadow-xl">
            <div className="mb-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="grid h-8 w-8 place-items-center rounded-lg bg-[#01C37D]/10 text-[#01C37D]">
                  <Settings2 size={16} />
                </div>
                <div>
                  <div className="text-sm font-semibold">
                    Update Payment Gateway
                  </div>
                  <div className="text-[11px] text-gray-500">
                    This will update gateway for <b>ALL clients</b>.
                  </div>
                </div>
              </div>
              <button
                className="h-7 rounded-xl border px-2 text-xs"
                onClick={() => setGatewayAllOpen(false)}
              >
                Close
              </button>
            </div>
            <div className="space-y-3">
              <label className="block text-sm">
                <div className="mb-1 text-xs text-gray-500">Gateway</div>
                <select
                  className="h-10 w-full rounded-xl border bg-white px-3 text-sm outline-none"
                  value={gatewayAll}
                  onChange={(e) => setGatewayAll(e.target.value)}
                >
                  <option value="">Select gateway</option>
                  {GateWay.map(
                    (gate, i) =>
                      gate.is_active && (
                        <option key={i} value={gate.gateway_name}>
                          {gate.gateway_name}
                        </option>
                      ),
                  )}
                </select>
              </label>
              <p className="text-[11px] text-amber-600">
                Tip: Use this only when you want to move all active clients to a
                new payment gateway in one shot.
              </p>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button
                className="h-8 rounded-xl border px-3 text-xs"
                onClick={() => setGatewayAllOpen(false)}
              >
                Cancel
              </button>
              <button
                className="inline-flex h-8 items-center justify-center rounded-xl bg-[#01C37D] px-4 text-xs font-medium text-white hover:bg-[#01C37D]/90 disabled:opacity-50"
                disabled={!gatewayAll || savingAllGateway}
                onClick={handleUpdateAllGateway}
              >
                {savingAllGateway ? "Updating…" : "Update All"}
              </button>
            </div>
          </div>
        </div>
      )}

      <RestaurantLatLongModal
        open={latLongOpen}
        onClose={() => setLatLongOpen(false)}
        getRestaurantPreciseLocation={getRestaurantPreciseLocation}
        updateRestaurantLatLong={updateRestaurantLatLong}
      />

      {/* Fixed assign menu — outside table so overflow cannot clip it */}
      {assignOpenFor && assignMenuPos && (
        <div
          data-assign-menu
          className="fixed z-[9999] w-48 rounded-xl border bg-white py-1 shadow-xl"
          style={{
            top: assignMenuPos.top,
            left: assignMenuPos.left,
          }}
        >
          <div className="px-3 py-1.5 text-[11px] font-medium text-gray-400">
            Assign location
          </div>
          {locationOptions.length === 0 ? (
            <div className="px-3 py-2 text-xs text-gray-500">No locations</div>
          ) : (
            locationOptions.map((loc) => (
              <button
                key={loc.id}
                type="button"
                disabled={assigning}
                className="flex w-full px-3 py-2 text-left text-xs hover:bg-emerald-50 disabled:opacity-50"
                onClick={() => {
                  if (assignClient) {
                    handleAssignLocation(assignClient, loc.id);
                  }
                }}
              >
                {loc.name}
              </button>
            ))
          )}
          <button
            type="button"
            className="mt-1 flex w-full border-t px-3 py-2 text-left text-xs text-gray-500 hover:bg-gray-50"
            onClick={closeAssignMenu}
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}

function Badge({ children, onClear }) {
  return (
    <span className="inline-flex items-center gap-2 rounded-full border bg-gray-50 px-3 py-1 text-xs">
      {children}
      <button
        className="rounded-full px-1.5 text-gray-500 hover:bg-gray-200"
        onClick={onClear}
      >
        ✕
      </button>
    </span>
  );
}

function Pagination({
  currentPage,
  totalPages,
  totalRecords,
  pageSize,
  onPageChange,
  loading = false,
}) {
  if (totalPages <= 1 && totalRecords <= pageSize) return null;

  const startRecord = totalRecords > 0 ? (currentPage - 1) * pageSize + 1 : 0;
  const endRecord = Math.min(currentPage * pageSize, totalRecords);

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-t bg-white px-4 py-3">
      <div className="text-xs text-gray-500">
        Showing{" "}
        <span className="font-semibold text-gray-700">{startRecord}</span>
        {" - "}
        <span className="font-semibold text-gray-700">{endRecord}</span>
        {" of "}
        <span className="font-semibold text-gray-700">{totalRecords}</span>
      </div>

      <div className="flex items-center gap-2">
        <button
          type="button"
          disabled={currentPage <= 1 || loading}
          onClick={() => onPageChange(currentPage - 1)}
          className="h-9 rounded-xl border px-3 text-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
        >
          Previous
        </button>

        <span className="min-w-[90px] text-center text-sm text-gray-600">
          Page{" "}
          <span className="font-semibold text-gray-900">{currentPage}</span>
          {" of "}
          <span className="font-semibold text-gray-900">{totalPages}</span>
        </span>

        <button
          type="button"
          disabled={currentPage >= totalPages || loading}
          onClick={() => onPageChange(currentPage + 1)}
          className="h-9 rounded-xl border px-3 text-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
        >
          Next
        </button>
      </div>
    </div>
  );
}